import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from "react-i18next";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
} from 'recharts';
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent, TileChartContent } from '../../../components/Tile/TileContent';
import { useLocalFilters } from '../../../hooks/useLocalFilters';
import { useOcelMetadata } from '../../../stores/OcelContext';
import TileSettingsModal from '../../../components/common/TileSettingsModal';
import LocalFiltersActiveBadge from '../../../components/common/LocalFiltersActiveBadge';
import BiaxialScatterPlotSettings from './BiaxialScatterPlotSettings';
import { useObjectTypeActivities } from './hooks/useObjectTypeActivities';
import { useObjectDateAttributes } from './hooks/useObjectDateAttributes';

const formatDateValue = (val) =>
  new Date(val * 1000).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const formatNumericValue = (val) => {
  if (Math.abs(val) >= 1_000_000) return `${(val / 1_000_000).toFixed(2)}M`;
  if (Math.abs(val) >= 1_000) return `${(val / 1_000).toFixed(1)}k`;
  return Number.isInteger(val) ? String(val) : val.toFixed(2);
};

const CustomTooltip = ({ active, payload, xLabel, yLabel, xIsDatetime, yIsDatetime }) => {
  if (!active || !payload?.length) return null;
  const { x, y, oid } = payload[0]?.payload || {};
  const fmtX = xIsDatetime ? formatDateValue(x) : formatNumericValue(x);
  const fmtY = yIsDatetime ? formatDateValue(y) : formatNumericValue(y);
  const deviation = xIsDatetime && yIsDatetime
    ? `${((y - x) / 86400).toFixed(1)} d`
    : (typeof x === 'number' && typeof y === 'number')
      ? formatNumericValue(y - x)
      : '—';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg text-xs space-y-1 max-w-[220px]">
      <div className="font-semibold text-gray-700 truncate">{oid}</div>
      <div className="text-gray-600"><span className="font-medium">{xLabel}:</span> {fmtX}</div>
      <div className="text-gray-600"><span className="font-medium">{yLabel}:</span> {fmtY}</div>
      <div className="text-gray-500 border-t border-gray-100 pt-1">Deviation (Y − X): {deviation}</div>
    </div>
  );
};

const DEFAULT_TITLE = 'Biaxial Scatter Plot';

const BiaxialScatterPlot = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    api,
    currentFilters,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  const metadata = useOcelMetadata(activeFile);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const isInitialMount = useRef(true);

  const [chartTitle, setChartTitle] = useState(DEFAULT_TITLE);
  const [objectType, setObjectType] = useState('');
  const [xAxisType, setXAxisType] = useState('object_attribute');
  const [xAxisValue, setXAxisValue] = useState('');
  const [yAxisType, setYAxisType] = useState('object_attribute');
  const [yAxisValue, setYAxisValue] = useState('');

  const objectTypes = useMemo(
    () => (metadata?.object_types ? Object.keys(metadata.object_types) : []),
    [metadata],
  );
  const eventAttrNames = useMemo(() => metadata?.event_attribute_names ?? [], [metadata]);

  const { activities: activitiesForType, loading: activitiesLoading } = useObjectTypeActivities(activeFile, objectType);
  const { dateAttributes: dateAttrsForType, loading: dateAttrsLoading } = useObjectDateAttributes(activeFile, objectType);

  // Sync title to parent header
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => { onTitleChangeRef.current?.(chartTitle || DEFAULT_TITLE); }, [chartTitle]);

  // Restore from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.chartTitle !== undefined) setChartTitle(initialSettings.chartTitle);
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.xAxisType !== undefined) setXAxisType(initialSettings.xAxisType);
    if (initialSettings.xAxisValue !== undefined) setXAxisValue(initialSettings.xAxisValue);
    if (initialSettings.yAxisType !== undefined) setYAxisType(initialSettings.yAxisType);
    if (initialSettings.yAxisValue !== undefined) setYAxisValue(initialSettings.yAxisValue);
    setPendingApply(true);
  }, []);

  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      chartTitle,
      objectType,
      xAxisType,
      xAxisValue,
      yAxisType,
      yAxisValue,
      localFilters,
    }));
  }, [chartTitle, objectType, xAxisType, xAxisValue, yAxisType, yAxisValue, localFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleObjectTypeChange = useCallback((val) => {
    setObjectType(val);
    setXAxisValue('');
    setYAxisValue('');
  }, []);

  const handleXAxisTypeChange = useCallback((val) => {
    setXAxisType(val);
    setXAxisValue('');
  }, []);

  const handleYAxisTypeChange = useCallback((val) => {
    setYAxisType(val);
    setYAxisValue('');
  }, []);

  const isFormValid = useCallback(
    () => !!(objectType && xAxisValue && yAxisValue),
    [objectType, xAxisValue, yAxisValue],
  );

  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change) must not overwrite a
  // newer one.
  const fetchSeqRef = useRef(0);
  const fetchData = useCallback(async () => {
    if (!activeFile) { setError('No active file selected.'); return; }
    if (!isFormValid()) { if (hasFetched) setError('Please fill in all required fields.'); return; }

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setHasFetched(true);
    try {
      const response = await api.post(
        '/scatter-plot',
        {
          object_type: objectType,
          x_axis_type: xAxisType,
          x_axis_value: xAxisValue,
          y_axis_type: yAxisType,
          y_axis_value: yAxisValue,
        },
        {},
        'Biaxial Scatter Plot',
      );
      if (seq !== fetchSeqRef.current) return;
      setData(response);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err.message || 'An unknown error occurred.');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [activeFile, objectType, xAxisType, xAxisValue, yAxisType, yAxisValue, api, hasFetched, isFormValid]);

  useEffect(() => {
    if (isInitialMount.current) return;
    if (hasFetched && !loading) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, activeFile]);

  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchData();
  }, [pendingApply, fetchData]);

  useEffect(() => { isInitialMount.current = false; }, []);

  useEffect(() => { if (onRefresh) onRefresh(fetchData); }, [onRefresh, fetchData]);

  const handleOpenSettings = useCallback((e) => {
    e.stopPropagation();
    startDraftMode();
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback((val) => {
    setIsSettingsModalOpen(val);
    if (!val) cancelDraftFilters();
  }, [cancelDraftFilters]);

  const handleApplySettings = useCallback(() => {
    applyDraftFilters();
    setPendingApply(true);
    setIsSettingsModalOpen(false);
  }, [applyDraftFilters]);

  const headerActions = useMemo(() => (
    <div className="flex items-center gap-2">
      {hasLocalFilters && (
        <LocalFiltersActiveBadge
          hasLocalFilters={hasLocalFilters}
          localFilterCount={localFilterCount}
          onClearLocalFilters={clearLocalFilters}
        />
      )}
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Open chart settings"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    </div>
  ), [handleOpenSettings, hasLocalFilters, localFilterCount, clearLocalFilters]);

  useEffect(() => { if (onGetHeaderActions) onGetHeaderActions(headerActions); }, [onGetHeaderActions, headerActions]);

  const renderChart = () => {
    if (!data?.points?.length) return null;

    const { points, x_axis_label, y_axis_label, x_is_datetime, y_is_datetime } = data;
    const allVals = [...points.map((p) => p.x), ...points.map((p) => p.y)];
    const dMin = Math.min(...allVals);
    const dMax = Math.max(...allVals);
    const pad = (dMax - dMin) * 0.05 || Math.abs(dMin) * 0.05 || 1;
    const domain = [dMin - pad, dMax + pad];
    const diagonalData = [{ x: domain[0], y: domain[0] }, { x: domain[1], y: domain[1] }];

    const tickFmt = x_is_datetime ? formatDateValue : formatNumericValue;
    const yTickFmt = y_is_datetime ? formatDateValue : formatNumericValue;

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 16, right: 24, bottom: 48, left: 56 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            type="number"
            dataKey="x"
            domain={domain}
            tickFormatter={tickFmt}
            tick={{ fontSize: 11 }}
          >
            <Label value={x_axis_label} position="insideBottom" offset={-30} style={{ fontSize: 12, fill: '#374151' }} />
          </XAxis>
          <YAxis
            type="number"
            dataKey="y"
            domain={domain}
            tickFormatter={yTickFmt}
            tick={{ fontSize: 11 }}
            width={80}
          >
            <Label value={y_axis_label} angle={-90} position="insideLeft" offset={-40} style={{ fontSize: 12, fill: '#374151', textAnchor: 'middle' }} />
          </YAxis>
          <Tooltip
            content={
              <CustomTooltip
                xLabel={x_axis_label}
                yLabel={y_axis_label}
                xIsDatetime={x_is_datetime}
                yIsDatetime={y_is_datetime}
              />
            }
            cursor={{ strokeDasharray: '3 3' }}
          />
          <Scatter data={points} fill="#4f46e5" opacity={0.7} />
          <Scatter
            data={diagonalData}
            line={{ stroke: '#9ca3af', strokeDasharray: '6 3', strokeWidth: 1.5 }}
            shape={() => null}
            legendType="none"
            name="X = Y"
          />
        </ScatterChart>
      </ResponsiveContainer>
    );
  };

  const showInitialButton = !hasFetched && !loading;
  const hasData = !loading && !error && data?.points?.length > 0;
  const hasEmptyData = !loading && !error && data && data.points_plotted === 0;

  return (
    <BaseTile loading={loading} error={error} minHeight="auto" padding="sm">
      <TileContent layout="flex" direction="column" grow>
        {showInitialButton && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={handleOpenSettings}
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Configure Chart
            </button>
          </div>
        )}

        {hasData && (
          <>
            <TileChartContent height={360} className="flex-grow">
              {renderChart()}
            </TileChartContent>
            {(data.points_missing_x > 0 || data.points_missing_y > 0 || data.points_plotted < data.total_plottable) && (
              <p className="text-xs text-gray-400 text-center mt-1 px-2">
                {data.points_plotted < data.total_plottable
                  ? `${data.points_plotted.toLocaleString('de-DE')} von ${data.total_plottable.toLocaleString('de-DE')} Objekten dargestellt (zufällige Auswahl)`
                  : `${data.points_plotted} von ${data.total_objects} Objekten`}
                {data.points_missing_x > 0 && ` · ${data.points_missing_x} ohne X-Wert`}
                {data.points_missing_y > 0 && ` · ${data.points_missing_y} ohne Y-Wert`}
              </p>
            )}
          </>
        )}

        {hasEmptyData && (
          <TileStatusContent
            status="empty"
            title="No Data Points"
            message="No objects with values for both selected axes. Try different attributes or adjust your filters."
            action={
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors"
              >
                Retry
              </button>
            }
          />
        )}

        {error && hasFetched && (
          <div className="flex justify-center mt-4">
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors"
            >
              Retry
            </button>
          </div>
        )}
      </TileContent>

      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        onApply={handleApplySettings}
        title="Biaxial Scatter Plot — Settings"
        isApplyDisabled={!activeFile || !isFormValid() || loading}
        applyButtonText={loading ? 'Loading…' : hasFetched ? 'Update Chart' : 'Show Chart'}
      >
        <BiaxialScatterPlotSettings
          chartTitle={chartTitle}
          onChartTitleChange={setChartTitle}
          objectType={objectType}
          onObjectTypeChange={handleObjectTypeChange}
          xAxisType={xAxisType}
          onXAxisTypeChange={handleXAxisTypeChange}
          xAxisValue={xAxisValue}
          onXAxisValueChange={setXAxisValue}
          yAxisType={yAxisType}
          onYAxisTypeChange={handleYAxisTypeChange}
          yAxisValue={yAxisValue}
          onYAxisValueChange={setYAxisValue}
          objectTypes={objectTypes}
          activitiesForType={activitiesForType}
          dateAttrsForType={dateAttrsForType}
          eventAttrNames={eventAttrNames}
          loading={loading}
          activitiesLoading={activitiesLoading}
          dateAttrsLoading={dateAttrsLoading}
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          activeFile={activeFile}
        />
      </TileSettingsModal>
    </BaseTile>
  );
};

export default BiaxialScatterPlot;
